import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import Modal from '@/components/Modal';
import SegmentedButton from '@/components/ui/SegmentedButton';
import { ExportPanel } from '@/features/vault/transfer/ExportPanel';
import { ImportPanel } from '@/features/vault/transfer/ImportPanel';
import { useAuthStore } from '@/stores/auth';

type TransferTab = 'export' | 'import';

/**
 * Cardul „Import și export" din Setări și dialogul pe care îl deschide.
 * Totul se întâmplă în browser: exportul decriptează local și criptează
 * fișierul cu o parolă aleasă de user, importul criptează local fiecare
 * element înainte să-l trimită.
 */
export function TransferCard() {
  const { t } = useTranslation();
  const { user, keyPair, currentTenantId } = useAuthStore();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<TransferTab>('export');
  const [busy, setBusy] = useState(false);

  const membership = user?.tenants.find((m) => m.tenant_id === currentTenantId);
  if (!membership || !keyPair) return null;

  return (
    <div>
      <div className="card flex items-center justify-between gap-4">
        <div>
          <h2 className="display-title-sm">{t('transfer.card_title')}</h2>
          <p className="text-xs text-[var(--text-muted)] mt-0.5">{t('transfer.card_hint')}</p>
        </div>
        <button type="button" className="btn-primary flex-shrink-0" onClick={() => setOpen(true)}>
          {t('transfer.open_button')}
        </button>
      </div>

      <Modal
        open={open}
        // Cât rulează un import sau un export, dialogul nu se închide din
        // greșeală (Escape, clic pe fundal) — se oprește din butonul lui.
        onClose={() => {
          if (!busy) setOpen(false);
        }}
        title={
          <div>
            <h2 className="display-title-sm">{t('transfer.dialog_title')}</h2>
            <p className="text-xs text-[var(--text-muted)] mt-0.5">
              {t('transfer.dialog_subtitle', { tenant: membership.name })}
            </p>
          </div>
        }
        maxWidth="max-w-xl"
      >
        <div className="space-y-4">
          {!busy && (
            <SegmentedButton<TransferTab>
              options={[
                { value: 'export', label: t('transfer.tab_export') },
                { value: 'import', label: t('transfer.tab_import') },
              ]}
              value={tab}
              onChange={setTab}
            />
          )}
          {tab === 'export' ? (
            <ExportPanel
              tenantId={membership.tenant_id}
              tenantSlug={membership.slug}
              keyPair={keyPair}
              onBusyChange={setBusy}
            />
          ) : (
            <ImportPanel
              tenantId={membership.tenant_id}
              publicKey={keyPair.publicKey}
              canWrite={membership.role !== 'viewer'}
              onBusyChange={setBusy}
            />
          )}
        </div>
      </Modal>
    </div>
  );
}
